// iter188 — Cloudflare-image aware <CFImage> with IntersectionObserver
// lazy loading + dark placeholder. Drops in wherever <img> was used.
//
// Usage:
//   <CFImage urls={photo}       variant="thumbnail" alt="…" className="…" />
//   <CFImage src="/api/files/…" variant="medium"    alt="…" className="…" />
//
// `urls` accepts either the object stored on business.photos entries
// ({thumbnail, medium, full, cf_id?}) or a plain URL string. When only
// a legacy URL is available we render it as-is (fallback path).
import { useEffect, useRef, useState } from "react";

const PLACEHOLDER = "#171717"; // opaque dark surface — matches card bg

function pickUrl(urls, variant) {
  if (!urls) return null;
  if (typeof urls === "string") return urls;
  return urls[variant] || urls.medium || urls.thumbnail || urls.full || null;
}

export default function CFImage({
  urls,
  src,
  variant = "medium",
  alt = "",
  className = "",
  style = {},
  loading = "lazy",
  testid,
}) {
  const source = pickUrl(urls || src, variant);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (loading === "eager") { setInView(true); return; }
    if (!("IntersectionObserver" in window)) { setInView(true); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setInView(true); io.disconnect(); break; }
        }
      },
      { rootMargin: "150px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading]);

  const mergedStyle = {
    background: PLACEHOLDER,
    transition: "opacity 260ms ease-out",
    opacity: loaded ? 1 : 0,
    ...style,
  };

  return (
    <div
      ref={ref}
      data-testid={testid || "cf-image"}
      className={className}
      style={{ position: "relative", background: PLACEHOLDER, overflow: "hidden" }}
    >
      {inView && source && (
        <img
          src={source}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true) /* still fade in the alt/blank */}
          style={{ ...mergedStyle, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </div>
  );
}
